import { useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { sessionExtendedApi } from "../services";
import { sessionListLimit } from "../shared/Constants";
import { Session } from "../types";
import { useParallelPaginatedLoading } from "./useParallelPaginatedLoading";

/**
 * Fetches all processed (completed) sessions in parallel when org session
 * count is known. Merged result is kept in local state. The org-switch
 * clearing of stale sessions is specific to this hook; the parallel fetch
 * mechanics are shared via useParallelPaginatedLoading.
 */
export const useCompletedSessionsLoading = () => {
  const [sessionList, setSessionList] = useState<Session[]>([]);
  const lastClearedOrgRef = useRef<string>("");

  const orgId = useAppSelector((state) => state.org).id;
  const completeCount = useAppSelector((state) => state.org.sessions.complete);
  const dispatch = useAppDispatch();

  // Clear stale sessions on org switch: reset tracking while the org has
  // sessions, then clear once when an org reports 0 completed.
  useEffect(() => {
    if (orgId && completeCount > 0) {
      lastClearedOrgRef.current = "";
    }
    if (
      orgId &&
      completeCount === 0 &&
      sessionList.length > 0 &&
      lastClearedOrgRef.current !== orgId
    ) {
      lastClearedOrgRef.current = orgId;
      setSessionList([]);
    }
  }, [orgId, completeCount, sessionList.length]);

  const { isFetching, fetchError, loadComplete } =
    useParallelPaginatedLoading<Session>({
      orgId,
      count: completeCount,
      limit: sessionListLimit,
      fetchKeyPrefix: "processed",
      makeRequest: (offset, limit) =>
        dispatch(
          sessionExtendedApi.endpoints.getSessionsByStatus.initiate({
            status: "processed",
            limit,
            offset,
          }),
        ),
      getId: (session) => session.id,
      alreadyLoadedCount: sessionList.length,
      onLoaded: setSessionList,
    });

  return {
    storedSessionList: sessionList,
    fetchingSessions: isFetching,
    fetchError,
    completeCount,
    sessionsLoadComplete: loadComplete,
  };
};
